import React, { useState, useEffect } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

type DeviceType = 'ios' | 'android' | 'desktop' | 'other';

const getDeviceType = (): DeviceType => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for iOS (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  }

  // Check for Android
  if (/android/.test(userAgent)) {
    return 'android';
  }

  // Check for desktop (Windows, Mac, Linux)
  if (/windows|macintosh|linux/.test(userAgent) && !/mobile/.test(userAgent)) {
    return 'desktop';
  }

  return 'other';
};

const isInStandaloneMode = (): boolean => {
  // Check display-mode media query
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // Check iOS standalone mode
  if ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone) {
    return true;
  }

  return false;
};

export const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deviceType, setDeviceType] = useState<DeviceType>('other');

  useEffect(() => {
    // Detect device type
    setDeviceType(getDeviceType());

    // Check if app is already installed
    if (isInStandaloneMode()) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently
    const dismissedDate = localStorage.getItem('pwa-install-dismissed');
    if (dismissedDate) {
      const daysSinceDismissed = (Date.now() - new Date(dismissedDate).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    const device = getDeviceType();

    // For iOS, show instructions after delay (no beforeinstallprompt event)
    if (device === 'ios') {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // For Android/Desktop Chrome, listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  if (!showPrompt || isInstalled) {
    return null;
  }

  // iOS-specific install instructions
  if (deviceType === 'ios') {
    return (
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 animate-slide-up">
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-primary-600" />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">
                Install VLDL Vendor Dashboard
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                Add this app to your home screen for quick access and offline support.
              </p>

              <div className="mt-3 space-y-2">
                <div className="flex items-center space-x-2 text-xs text-gray-700">
                  <span className="flex items-center justify-center w-5 h-5 bg-gray-100 rounded text-[10px] font-medium">1</span>
                  <span className="flex items-center">
                    Tap the <Share className="w-4 h-4 mx-1 text-primary-600" /> Share button
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-700">
                  <span className="flex items-center justify-center w-5 h-5 bg-gray-100 rounded text-[10px] font-medium">2</span>
                  <span className="flex items-center">
                    Scroll and tap <Plus className="w-4 h-4 mx-1 text-primary-600" /> "Add to Home Screen"
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-700">
                  <span className="flex items-center justify-center w-5 h-5 bg-gray-100 rounded text-[10px] font-medium">3</span>
                  <span>Tap "Add" to confirm</span>
                </div>
              </div>

              <button
                onClick={handleDismiss}
                className="mt-3 w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors touch-manipulation border border-gray-200 rounded-lg"
              >
                Got it
              </button>
            </div>

            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors touch-manipulation"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop install prompt (requires deferredPrompt)
  if (!deferredPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 animate-slide-up">
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <Download className="w-5 h-5 text-primary-600" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">
              Install VLDL Vendor Dashboard
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Install the app for faster access and offline support. Works like a native app!
            </p>

            <div className="flex items-center space-x-2 mt-3">
              <button
                onClick={handleInstallClick}
                className="flex-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors touch-manipulation"
              >
                Install App
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors touch-manipulation"
              >
                Not Now
              </button>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors touch-manipulation"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};